import React, { useEffect, useState, useMemo } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Typography,
  TextField,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Checkbox,
  CircularProgress,
  Alert,
  useTheme,
  useMediaQuery,
  Divider
} from '@mui/material';
import { BusinessOutlined, Search as SearchIcon, InfoOutlined } from '@mui/icons-material';
import apiService from '../../services/api.service';

interface Company {
  CompanyID: number;
  CompanyName: string;
  DatabaseName?: string;
}

interface CompanySelectionDialogProps {
  open: boolean;
  onClose: () => void;
  onCompanySelected: (companyId: number, companyName: string, databaseName: string) => void;
}

const CompanySelectionDialog: React.FC<CompanySelectionDialogProps> = ({
  open,
  onClose: _onClose,
  onCompanySelected,
}) => {
  const theme = useTheme();
  const fullScreen = useMediaQuery(theme.breakpoints.down('sm'));
  const [companies, setCompanies] = useState<Company[]>([]);
  const [selectedCompanyId, setSelectedCompanyId] = useState<number | undefined>(undefined);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [searchQuery, setSearchQuery] = useState('');

  // Filter companies based on search
  const filteredCompanies = useMemo(() => {
    if (!searchQuery.trim()) return companies;
    const query = searchQuery.toLowerCase();
    return companies.filter(
      (c) =>
        String(c.CompanyID).includes(query) ||
        c.CompanyName.toLowerCase().includes(query) ||
        (c.DatabaseName && c.DatabaseName.toLowerCase().includes(query))
    );
  }, [companies, searchQuery]);

  useEffect(() => {
    if (!open) return;

    const loadCompanies = async () => {
      setLoading(true);
      setError(null);
      setSearchQuery('');
      try {
        let list: Array<{ CompanyID: number; CompanyName: string; DatabaseName?: string }> = [];

        // First try to get user's assigned companies
        try {
          const resUser = await apiService.getMyCompanies(true);
          const rawUser = Array.isArray(resUser) ? resUser : (resUser as any)?.data || [];
          list = rawUser.map((c: any) => ({
            CompanyID: c.CompanyID ?? c.companyid,
            CompanyName: c.CompanyName ?? c.companyname,
            DatabaseName: c.DatabaseName ?? c.databasename
          }));
        } catch (e) {
          console.log('Failed to load user companies, trying all companies');
        }

        // If no user companies, try to get all companies
        if (!list.length) {
          try {
            const resAll = await apiService.getCompanies(true);
            const rawAll = Array.isArray(resAll) ? resAll : (resAll as any)?.data || [];
            list = rawAll.map((c: any) => ({
              CompanyID: c.CompanyID ?? c.companyid,
              CompanyName: c.CompanyName ?? c.companyname,
              DatabaseName: c.DatabaseName ?? c.databasename
            }));
          } catch (e) {
            console.log('Failed to load all companies');
          }
        }

        // Sort by CompanyID
        list.sort((a, b) => a.CompanyID - b.CompanyID);
        setCompanies(list);

        // Auto-select first company if only one exists
        if (list.length === 1) {
          const company = list[0];
          setSelectedCompanyId(company.CompanyID);
          setTimeout(() => {
            onCompanySelected(company.CompanyID, company.CompanyName, company.DatabaseName || '');
          }, 500);
        }
      } catch (err: any) {
        setError(err?.response?.data?.message || 'Failed to load companies');
      } finally {
        setLoading(false);
      }
    };

    loadCompanies();
  }, [open, onCompanySelected]);

  const handleRowClick = (companyId: number) => {
    setSelectedCompanyId(companyId);
  };

  const handleConfirm = () => {
    if (!selectedCompanyId) {
      setError('Please select a company');
      return;
    }

    const company = companies.find((c) => c.CompanyID === selectedCompanyId);
    if (company) {
      onCompanySelected(company.CompanyID, company.CompanyName, company.DatabaseName || '');
    }
  };

  const handleDoubleClick = (companyId: number) => {
    const company = companies.find((c) => c.CompanyID === companyId);
    if (company) {
      onCompanySelected(company.CompanyID, company.CompanyName, company.DatabaseName || '');
    }
  };

  return (
    <Dialog
      open={open}
      onClose={() => {}}
      disableEscapeKeyDown
      maxWidth="sm"
      fullWidth
      fullScreen={fullScreen}
      PaperProps={{
        sx: {
          borderRadius: fullScreen ? 0 : 1,
          height: fullScreen ? '100%' : 'auto',
          maxHeight: fullScreen ? '100%' : '75vh',
          border: '1px solid #d9d9d9',
          boxShadow: '0 0 10px rgba(0, 0, 0, 0.1)'
        }
      }}
    >
      <DialogTitle sx={{
        py: 1.5,
        px: 2,
        backgroundColor: '#2c3e50',
        color: '#ffffff',
        fontSize: '0.95rem',
        fontWeight: 600,
        borderBottom: '1px solid #d9d9d9',
        display: 'flex',
        alignItems: 'center',
        gap: 1
      }}>
        <BusinessOutlined sx={{ fontSize: 20 }} />
        <Box sx={{ flexGrow: 1 }}>
          Select Company
          {companies.length > 1 && (
            <Typography component="span" sx={{ ml: 1, fontSize: '0.8rem', fontWeight: 400, opacity: 0.9 }}>
              ({companies.length} available)
            </Typography>
          )}
        </Box>
      </DialogTitle>

      <DialogContent sx={{ p: 0, minHeight: 300, maxHeight: 'calc(75vh - 100px)', overflow: 'hidden' }}>
        {loading ? (
          <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', py: 8, gap: 1.5 }}>
            <CircularProgress size={28} sx={{ color: '#0a6ed1', thickness: 3 }} />
            <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.85rem' }}>
              Loading companies...
            </Typography>
          </Box>
        ) : error ? (
          <Alert severity="error" sx={{ m: 2, py: 1 }}>
            {error}
          </Alert>
        ) : companies.length === 0 ? (
          <Alert severity="warning" sx={{ m: 2, py: 1 }}>
            No companies found. Please contact your administrator.
          </Alert>
        ) : (
          <>
            {companies.length > 1 && (
              <Box sx={{ px: 2, py: 1.5, borderBottom: '1px solid #e5e5e5', backgroundColor: '#fafafa' }}>
                <TextField
                  fullWidth
                  size="small"
                  placeholder="Search..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  InputProps={{
                    startAdornment: <SearchIcon sx={{ mr: 1, color: 'text.secondary', fontSize: 18 }} />,
                    sx: {
                      fontSize: '0.85rem',
                      height: 32,
                      backgroundColor: '#ffffff'
                    }
                  }}
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      height: 32,
                      '& fieldset': {
                        borderWidth: '1px',
                        borderColor: '#d9d9d9'
                      },
                      '&.Mui-focused fieldset': {
                        borderWidth: '1px',
                        borderColor: '#0a6ed1',
                      },
                    },
                  }}
                />
              </Box>
            )}

            <TableContainer sx={{ maxHeight: 'calc(75vh - 200px)' }}>
              <Table size="small" stickyHeader sx={{ minWidth: '100%' }}>
                <TableHead>
                  <TableRow sx={{
                    backgroundColor: '#f5f5f5',
                    '& .MuiTableCell-root': {
                      fontWeight: 600,
                      fontSize: '0.75rem',
                      py: 0.75,
                      px: 1.5,
                      color: '#2c3e50',
                      textTransform: 'uppercase',
                      letterSpacing: '0.5px',
                      borderBottom: '1px solid #d9d9d9',
                      backgroundColor: '#f5f5f5'
                    }
                  }}>
                    <TableCell padding="checkbox" sx={{ width: 40 }} />
                    <TableCell sx={{ width: 70 }}>ID</TableCell>
                    <TableCell>Company Name</TableCell>
                    <TableCell sx={{ width: 100 }}>Database</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {filteredCompanies.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={4} align="center" sx={{ py: 3 }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 1, color: 'text.secondary' }}>
                          <InfoOutlined sx={{ fontSize: 18 }} />
                          <Typography variant="body2" sx={{ fontSize: '0.85rem' }}>
                            No companies match your search
                          </Typography>
                        </Box>
                      </TableCell>
                    </TableRow>
                  ) : (
                    filteredCompanies.map((company) => (
                      <TableRow
                        key={company.CompanyID}
                        hover
                        selected={selectedCompanyId === company.CompanyID}
                        onClick={() => handleRowClick(company.CompanyID)}
                        onDoubleClick={() => handleDoubleClick(company.CompanyID)}
                        sx={{
                          cursor: 'pointer',
                          backgroundColor: selectedCompanyId === company.CompanyID ? '#ebf5ff' : 'inherit',
                          '&.Mui-selected': {
                            backgroundColor: '#ebf5ff',
                            '&:hover': {
                              backgroundColor: '#d0e8ff',
                            },
                          },
                          '&:hover': {
                            backgroundColor: selectedCompanyId === company.CompanyID ? '#d0e8ff' : '#f5f5f5'
                          },
                          '& .MuiTableCell-root': {
                            py: 0.75,
                            px: 1.5,
                            fontSize: '0.85rem',
                            borderBottom: '1px solid #e5e5e5',
                            color: '#2c3e50'
                          },
                        }}
                      >
                        <TableCell padding="checkbox">
                          <Checkbox
                            checked={selectedCompanyId === company.CompanyID}
                            size="small"
                            sx={{
                              color: '#0a6ed1',
                              padding: 0,
                              '&.Mui-checked': {
                                color: '#0a6ed1',
                              },
                            }}
                          />
                        </TableCell>
                        <TableCell sx={{ fontWeight: 600, color: '#0a6ed1', fontSize: '0.85rem' }}>
                          {company.CompanyID}
                        </TableCell>
                        <TableCell sx={{ fontWeight: 400 }}>
                          {company.CompanyName}
                        </TableCell>
                        <TableCell sx={{ color: '#757575', fontFamily: 'Consolas, monospace', fontSize: '0.8rem' }}>
                          {company.DatabaseName || '-'}
                        </TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          </>
        )}
      </DialogContent>

      {!loading && companies.length > 1 && (
        <>
          <Divider />
          <DialogActions sx={{
            px: 2,
            py: 1,
            backgroundColor: '#fafafa',
            justifyContent: 'space-between',
            minHeight: 44
          }}>
            <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.8rem', fontStyle: 'normal' }}>
              {selectedCompanyId
                ? `Selected: ${companies.find(c => c.CompanyID === selectedCompanyId)?.CompanyName}`
                : 'No company selected'}
            </Typography>
            <Button
              onClick={handleConfirm}
              variant="contained"
              size="small"
              disabled={!selectedCompanyId}
              sx={{
                minWidth: 120,
                py: 0.5,
                px: 2,
                fontSize: '0.85rem',
                bgcolor: '#0a6ed1',
                fontWeight: 500,
                textTransform: 'none',
                boxShadow: 'none',
                '&:hover': {
                  bgcolor: '#0855a0',
                  boxShadow: 'none'
                },
                '&:disabled': {
                  bgcolor: '#e0e0e0',
                  color: '#9e9e9e'
                }
              }}
            >
              Continue
            </Button>
          </DialogActions>
        </>
      )}
    </Dialog>
  );
};

export default CompanySelectionDialog;
